import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, Pencil, X, Upload, Trophy, ExternalLink } from "lucide-react";
import { api, API, apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { platform: "Roblox", title: "", description: "", date: "", link: "", image_url: "", metric_value: "", metric_label: "" };
export const fileSrc = (u) => (u?.startsWith("/api/") ? `${API}${u.slice(4)}` : u);
const inp = "h-9 border-zinc-800 bg-zinc-950 text-[13px] text-zinc-100";

function Field({ label, children }) {
  return <div><div className="mb-1 text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>{children}</div>;
}

export function ModAchievements() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = () => api.get("/achievements").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = () => { setEditId(null); setForm(EMPTY); };
  const startEdit = (a) => { setEditId(a.id); setForm({ ...EMPTY, ...a }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: r.data.url }));
    } catch (e) {
      toast.error(apiErr(e?.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.platform.trim() || !form.title.trim()) return toast.error("Platform és cím kötelező");
    setSaving(true);
    const { id, created_at, updated_at, ...body } = form;
    try {
      if (editId) await api.put(`/mod/achievements/${editId}`, body);
      else await api.post("/mod/achievements", body);
      toast.success(editId ? "Eredmény frissítve" : "Eredmény létrehozva");
      reset();
      await load();
    } catch (e) {
      toast.error(apiErr(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (aid) => {
    await api.delete(`/mod/achievements/${aid}`);
    if (editId === aid) reset();
    await load();
    toast.success("Eredmény törölve");
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="mod-achievements">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-zinc-50">Eredmények</h2>
          <p className="text-[12px] text-zinc-500">Ezek jelennek meg a nyilvános <span className="font-mono">/results</span> oldalon.</p>
        </div>
        <a href="/results" target="_blank" rel="noreferrer" data-testid="achievements-open-public" className="inline-flex items-center gap-1.5 text-[12px] text-blue-400 hover:underline"><ExternalLink size={13} /> Nyilvános oldal</a>
      </div>

      <div className="rounded-xl border border-zinc-800/70 bg-[#111113] p-4">
        <div className="mb-3 flex items-center gap-2 text-[13px] font-medium text-zinc-200">
          {editId ? <Pencil size={15} className="text-blue-400" /> : <Plus size={15} className="text-blue-400" />}
          {editId ? "Eredmény szerkesztése" : "Új eredmény"}
          {editId && <button data-testid="achievement-cancel-edit" onClick={reset} className="ml-auto inline-flex items-center gap-1 text-[12px] text-zinc-500 hover:text-zinc-300"><X size={13} /> Mégse</button>}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Platform *"><Input data-testid="achievement-platform" value={form.platform} onChange={set("platform")} placeholder="pl. Roblox" className={inp} /></Field>
          <Field label="Dátum"><Input data-testid="achievement-date" type="date" value={form.date} onChange={set("date")} className={inp} /></Field>
          <div className="sm:col-span-2"><Field label="Cím *"><Input data-testid="achievement-title" value={form.title} onChange={set("title")} className={inp} /></Field></div>
          <Field label="Kiemelt számadat"><Input data-testid="achievement-metric-value" value={form.metric_value} onChange={set("metric_value")} placeholder="pl. 1 200" className={inp} /></Field>
          <Field label="Számadat felirata"><Input data-testid="achievement-metric-label" value={form.metric_label} onChange={set("metric_label")} placeholder="pl. eltávolított fiók" className={inp} /></Field>
          <div className="sm:col-span-2"><Field label="Link (opcionális)"><Input data-testid="achievement-link" value={form.link} onChange={set("link")} placeholder="https://…" className={inp} /></Field></div>
        </div>
        <div className="mt-3"><Field label="Rövid leírás"><Textarea data-testid="achievement-description" value={form.description} onChange={set("description")} className="min-h-[90px] border-zinc-800 bg-zinc-950 text-[13px] text-zinc-100" /></Field></div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="achievement-image-input" onChange={(e) => upload(e.target.files?.[0])} />
          <Button data-testid="achievement-image-upload" type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-9 gap-2 border-zinc-800 bg-zinc-900 text-[12px] text-zinc-300">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Kép feltöltése
          </Button>
          {form.image_url && (
            <span className="flex items-center gap-2">
              <img src={fileSrc(form.image_url)} alt="" className="h-10 w-16 rounded object-cover" data-testid="achievement-image-preview" />
              <button data-testid="achievement-image-remove" onClick={() => setForm({ ...form, image_url: "" })} className="text-zinc-500 hover:text-red-400"><X size={14} /></button>
            </span>
          )}
          <Button data-testid="achievement-save" onClick={save} disabled={saving} className="ml-auto h-9 gap-2 bg-blue-600 px-5 text-[13px] font-semibold text-white hover:bg-blue-500">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {editId ? "Mentés" : "Létrehozás"}
          </Button>
        </div>
      </div>

      <div className="mt-5 space-y-2">
        {items.length === 0 && <div className="rounded-lg border border-zinc-800/70 bg-[#111113] p-6 text-center text-[13px] text-zinc-600">Még nincs eredmény.</div>}
        {items.map((a) => (
          <div key={a.id} data-testid={`achievement-item-${a.id}`} className="flex items-start gap-3 rounded-lg border border-zinc-800/70 bg-[#111113] p-3.5">
            {a.image_url ? <img src={fileSrc(a.image_url)} alt="" className="h-12 w-16 shrink-0 rounded object-cover" /> : <div className="grid h-12 w-16 shrink-0 place-items-center rounded bg-zinc-900"><Trophy size={16} className="text-yellow-400" /></div>}
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500">{a.platform}{a.date && ` · ${a.date}`}</div>
              <div className="text-[14px] font-medium text-zinc-100">{a.title}</div>
              {a.metric_value && <div className="text-[12px] text-yellow-300">{a.metric_value} {a.metric_label}</div>}
            </div>
            <button data-testid={`achievement-edit-${a.id}`} onClick={() => startEdit(a)} className="grid h-8 w-8 place-items-center rounded-md text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"><Pencil size={14} /></button>
            <button data-testid={`achievement-delete-${a.id}`} onClick={() => remove(a.id)} className="grid h-8 w-8 place-items-center rounded-md text-red-400/70 hover:bg-red-950/40 hover:text-red-300"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
